import axios from "axios";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";

const MODULO = "Obligaciones";

const RegistrarObligaciones = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const isEdit = location.pathname.includes("/editar/");

  const [observacion, setObservacion] = useState("");
  const [loading, setLoading] = useState(false);

  const hasId = useMemo(() => Boolean(id), [id]);

  const authConfig = () => {
    const token = localStorage.getItem("token");

    return {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
  };

  const normalizarTexto = (texto) =>
    String(texto || "")
      .trim()
      .toLowerCase()
      .replace(/\s+/g, "");

  const getModuloObligaciones = () => {
    try {
      const permisosStorage = localStorage.getItem("permisos");
      const usuarioStorage = localStorage.getItem("usuario");
      const userStorage = localStorage.getItem("user");
      const usuariosStorage = localStorage.getItem("usuarios");

      const permisosData = permisosStorage
        ? JSON.parse(permisosStorage)
        : usuarioStorage
          ? JSON.parse(usuarioStorage)
          : userStorage
            ? JSON.parse(userStorage)
            : usuariosStorage
              ? JSON.parse(usuariosStorage)
              : {};

      const modulos = Array.isArray(permisosData)
        ? permisosData
        : permisosData?.modulos ||
        permisosData?.permisos ||
        permisosData?.usuario?.modulos ||
        permisosData?.usuario?.permisos ||
        permisosData?.user?.modulos ||
        permisosData?.user?.permisos ||
        [];

      const moduloBuscado = normalizarTexto(MODULO);

      return (
        modulos.find((modulo) => {
          const nombre =
            modulo?.nombreModulo || modulo?.modulo || modulo?.nombre || "";

          return normalizarTexto(nombre) === moduloBuscado;
        }) || {}
      );
    } catch (error) {
      console.error("Error leyendo permisos:", error);
      return {};
    }
  };

  const moduloObligaciones = getModuloObligaciones();

  const puedeCrear =
    moduloObligaciones.puedeCrear === true ||
    moduloObligaciones.crear === true;

  const puedeActualizar =
    moduloObligaciones.puedeActualizar === true ||
    moduloObligaciones.actualizar === true;

  const tienePermiso = isEdit ? puedeActualizar : puedeCrear;

  const disabledAll = loading || !tienePermiso;

  const cleanData = () => {
    setObservacion("");
  };

  const getObligacionById = async () => {
    if (!hasId) return;

    try {
      setLoading(true);

      const { data } = await axios.get(
        `/api/obligaciones/listid/${id}`,
        authConfig()
      );

      const obligacion = data.obligacion || data.message || data;

      setObservacion(obligacion?.observacion || "");
    } catch (error) {
      console.error("Error al cargar la obligación:", error);

      Swal.fire({
        icon: "error",
        title: "Error",
        text:
          error.response?.data?.message ||
          "No se pudo cargar la información de la obligación",
      });
    } finally {
      setLoading(false);
    }
  };

  const saveObligaciones = async () => {
    const response = await axios.post(
      "/api/obligaciones/add",
      {
        contratoObjectId: id,
        observacion,
      },
      authConfig()
    );

    if (response.data.ok) {
      await Swal.fire({
        icon: "success",
        title: "Obligación registrada correctamente",
        showConfirmButton: false,
        timer: 1500,
      });

      cleanData();
      navigate(-1);
    } else {
      Swal.fire({
        icon: "error",
        title: "No se pudo registrar",
        text: response.data.message || "Error inesperado",
      });
    }
  };

  const updateObligaciones = async () => {
    const response = await axios.put(
      `/api/obligaciones/update/${id}`,
      {
        observacion,
      },
      authConfig()
    );

    if (response.data.ok) {
      await Swal.fire({
        icon: "success",
        title: "Obligación actualizada correctamente",
        showConfirmButton: false,
        timer: 1500,
      });

      navigate(-1);
    } else {
      Swal.fire({
        icon: "error",
        title: "No se pudo actualizar",
        text: response.data.message || "Error inesperado",
      });
    }
  };

  const actions = async (e) => {
    e.preventDefault();

    if (!tienePermiso) {
      Swal.fire(
        "Sin permisos",
        isEdit
          ? "No tiene permisos para actualizar obligaciones."
          : "No tiene permisos para crear obligaciones.",
        "warning"
      );
      return;
    }

    if (!observacion.trim()) {
      Swal.fire(
        "Campo requerido",
        "Debe ingresar la actividad u obligación.",
        "warning"
      );
      return;
    }

    if (!hasId) {
      Swal.fire({
        icon: "error",
        title: "Falta información",
        text: isEdit
          ? "No se recibió el ID de la obligación en la URL."
          : "No se recibió el ID del contrato en la URL.",
      });
      return;
    }

    try {
      setLoading(true);

      if (isEdit) {
        await updateObligaciones();
      } else {
        await saveObligaciones();
      }
    } catch (error) {
      console.error("Error al guardar la obligación:", error);

      Swal.fire({
        icon: "error",
        title: "Error",
        text:
          error.response?.data?.message ||
          "No se pudo guardar la obligación",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isEdit && id) {
      getObligacionById();
    }
  }, [isEdit, id]);

  if (!tienePermiso) {
    return (
      <div className="container py-5">
        <div className="alert alert-warning">
          {isEdit
            ? "No tiene permisos para actualizar obligaciones."
            : "No tiene permisos para crear obligaciones."}
        </div>

        <button
          className="btn btn-dark"
          type="button"
          onClick={() => navigate(-1)}
        >
          Volver
        </button>
      </div>
    );
  }

  return (
    <div className="bg-body-tertiary py-4">
      <div className="container-fluid px-2 px-md-4">
        <div className="bg-white border rounded-4 shadow-sm overflow-hidden">
          <div className="px-3 px-md-4 py-3 border-bottom bg-white">
            <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
              <div>
                <h5 className="mt-2 mb-1 fw-semibold">
                  {isEdit
                    ? "Editar Obligación Contractual"
                    : "Registrar Obligación Contractual"}
                </h5>

                <small className="text-secondary">
                  {isEdit
                    ? "Actualiza la información de la obligación contractual."
                    : "Registra una actividad u obligación asociada al contrato."}
                </small>
              </div>

              <div className="d-flex gap-2 flex-nowrap">
                <button
                  type="button"
                  className="btn btn-outline-dark"
                  onClick={() => navigate(-1)}
                  disabled={loading}
                  title="Volver"
                >
                  <i className="fa-solid fa-arrow-rotate-left me-2"></i>
                  Volver
                </button>

                {!isEdit && (
                  <button
                    type="button"
                    className="btn btn-outline-dark"
                    onClick={cleanData}
                    disabled={loading}
                    title="Limpiar"
                  >
                    <i className="fa-solid fa-eraser me-2"></i>
                    Limpiar
                  </button>
                )}

                <button
                  type="submit"
                  form="form-obligacion"
                  className="btn btn-dark"
                  disabled={disabledAll}
                  title={
                    isEdit
                      ? "Actualizar obligación"
                      : "Guardar obligación"
                  }
                >
                  <i
                    className={`fa-solid ${loading
                        ? "fa-spinner fa-spin"
                        : "fa-floppy-disk"
                      } me-2`}
                  ></i>

                  {isEdit ? "Actualizar" : "Guardar"}
                </button>
              </div>
            </div>
          </div>

          <div className="p-3 p-md-4">
            {loading && (
              <div className="text-center text-secondary py-3">
                <div className="spinner-border" role="status" />
                <div className="mt-2">Procesando…</div>
              </div>
            )}

            <form
              id="form-obligacion"
              onSubmit={actions}
              className="needs-validation"
              noValidate
            >
              <div className="row g-3">
                <div className="col-12">
                  <label className="form-label fw-semibold">
                    Actividad u Obligación
                  </label>

                  <textarea
                    className="form-control"
                    rows="6"
                    value={observacion}
                    onChange={(e) => setObservacion(e.target.value)}
                    placeholder="Describa la actividad u obligación a realizar..."
                    required
                    disabled={disabledAll}
                  />

                  <div className="form-text">
                    Recomendación: escribe información clara.
                  </div>
                </div>

                <div className="col-12 pt-2">
                  <>
                    {/* ESCRITORIO */}
                    <div className="d-none d-md-flex flex-wrap gap-2 justify-content-end">
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={() => navigate(-1)}
                        disabled={loading}
                        title="Volver"
                      >
                        <i className="fa-solid fa-arrow-rotate-left me-2"></i>
                        Volver
                      </button>

                      <button
                        type="button"
                        className="btn btn-outline-dark"
                        onClick={cleanData}
                        disabled={disabledAll}
                        title="Limpiar"
                      >
                        <i className="fa-solid fa-eraser me-2"></i>
                        Limpiar
                      </button>

                      <button
                        type="submit"
                        form="form-obligacion"
                        className="btn btn-dark"
                        disabled={disabledAll}
                        style={{
                          opacity: tienePermiso ? 1 : 0.5,
                          cursor: tienePermiso ? "pointer" : "not-allowed",
                        }}
                        title={
                          isEdit
                            ? "Actualizar obligación"
                            : "Registrar obligación"
                        }
                      >
                        <i
                          className={`fa-solid ${loading
                              ? "fa-spinner fa-spin"
                              : "fa-floppy-disk"
                            } me-2`}
                        ></i>

                        {isEdit ? "Actualizar" : "Guardar"}
                      </button>
                    </div>

                    {/* RESPONSIVE */}
                    <div className="d-flex d-md-none gap-2 flex-nowrap">
                      <button
                        type="button"
                        className="btn btn-outline-secondary shadow-sm flex-fill d-flex flex-column align-items-center justify-content-center"
                        onClick={() => navigate(-1)}
                        disabled={loading}
                        title="Volver"
                      >
                        <i className="fa-solid fa-arrow-rotate-left mb-1"></i>
                        <small>Volver</small>
                      </button>

                      <button
                        type="button"
                        className="btn btn-outline-dark shadow-sm flex-fill d-flex flex-column align-items-center justify-content-center"
                        onClick={cleanData}
                        disabled={disabledAll}
                        title="Limpiar"
                      >
                        <i className="fa-solid fa-eraser mb-1"></i>
                        <small>Limpiar</small>
                      </button>

                      <button
                        type="submit"
                        form="form-obligacion"
                        className="btn btn-dark flex-fill d-flex flex-column align-items-center justify-content-center"
                        disabled={disabledAll}
                        style={{
                          opacity: tienePermiso ? 1 : 0.5,
                          cursor: tienePermiso ? "pointer" : "not-allowed",
                        }}
                        title={
                          isEdit
                            ? "Actualizar obligación"
                            : "Registrar obligación"
                        }
                      >
                        <i
                          className={`fa-solid ${loading
                              ? "fa-spinner fa-spin"
                              : "fa-floppy-disk"
                            } mb-1`}
                        ></i>

                        <small>
                          {isEdit ? "Actualizar" : "Guardar"}
                        </small>
                      </button>
                    </div>
                  </>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegistrarObligaciones;